/**
 * React Render Tracking via DevTools Hook
 *
 * This file sets up the __REACT_DEVTOOLS_GLOBAL_HOOK__ to intercept fiber commits
 * and track component renders with parent relationships.
 *
 * Approach based on bippy/React DevTools:
 * - Hook into onCommitFiberRoot
 * - Traverse the committed fiber tree
 * - For each rendered fiber, walk fiber.return to get parent
 * - Log render events directly during traversal
 *
 * MUST be imported before React loads.
 *
 * Usage:
 *   // At the very top of your main.tsx, before any React imports:
 *   import 'encamp-metrics/devtools-hook';
 *   import React from 'react';
 *   // ...
 */
import { bolt } from "./Bolt.js";
// Fiber tags (from React source)
const FunctionComponent = 0;
const ClassComponent = 1;
const ForwardRef = 11;
const SimpleMemoComponent = 15;
const MemoComponent = 14;
// Fiber flags - indicates work was performed
const PerformedWork = 0b1;
const Update = 0b100;
// ============================================
// Fiber utilities (based on bippy patterns)
// ============================================
function getDisplayName(type) {
    if (!type)
        return null;
    if (typeof type === "string")
        return null; // DOM element
    const t = type;
    // Direct displayName or name
    if (t.displayName)
        return t.displayName;
    if (typeof type === "function" && t.name)
        return t.name;
    // Unwrap memo/forwardRef
    if (typeof type === "object") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inner = type.type || type.render;
        if (inner)
            return getDisplayName(inner);
    }
    return null;
}
function isComponentFiber(fiber) {
    return (fiber.tag === FunctionComponent ||
        fiber.tag === ClassComponent ||
        fiber.tag === ForwardRef ||
        fiber.tag === SimpleMemoComponent ||
        fiber.tag === MemoComponent);
}
function shouldSkipFiber(name) {
    if (!name)
        return true;
    // Skip styled-components
    if (name.startsWith("Styled("))
        return true;
    // Skip React internals
    if (name === "Fragment" || name === "Suspense" || name === "Memo" || name === "ForwardRef")
        return true;
    // Skip MUI components (Box, ThemeProvider, etc.)
    if (name.match(/^(Box|Stack|Grid|Paper|Container|Typography)\d*$/))
        return true;
    if (name.match(/^(Theme|Rtl|DefaultProps)Provider\d*$/))
        return true;
    if (name.match(/^ThemeProviderNoVars$/))
        return true;
    // Skip router internals
    if (name === "RenderedRoute" || name === "RenderErrorBoundary" || name === "Outlet")
        return true;
    if (name === "Router" || name === "DataRoutes")
        return true;
    // Skip context providers that are infrastructure
    if (name.match(/^(RouterProvider|QueryParamProvider)\d*$/))
        return true;
    return false;
}
function getParentComponentName(fiber) {
    let parent = fiber.return;
    let depth = 0;
    while (parent && depth < 100) {
        depth++;
        if (isComponentFiber(parent)) {
            const name = getDisplayName(parent.type);
            if (name && !shouldSkipFiber(name)) {
                return name;
            }
        }
        parent = parent.return;
    }
    return null;
}
function didFiberRender(fiber) {
    // Check if fiber performed work this commit
    const dominated = fiber.alternate === null; // New mount
    // React 19 uses different flag values - check ANY flags instead of specific bits
    // The caller (collectRenderedFibers) already filters to fibers with flags !== 0
    const hasAnyFlags = fiber.flags !== 0;
    // For function components: trust ANY flags as indication of render
    // React 19 uses different flag bits than React 18
    if (fiber.tag === FunctionComponent) {
        return dominated || hasAnyFlags;
    }
    return dominated || hasAnyFlags;
}
/**
 * Registry of context objects to their names (populated during fiber traversal)
 * This lets us match context dependencies to their Provider names
 */
const contextRegistry = new WeakMap();
/**
 * Context Provider fiber tag
 */
const ContextProvider = 10;
/**
 * Walk the fiber tree to find and register all Context Providers.
 * OPTIMIZED: Uses subtreeFlags to skip subtrees with no work.
 * This populates contextRegistry so we can look up names later.
 */
function registerContextProviders(fiber) {
    if (!fiber)
        return;
    // OPTIMIZATION: Skip subtrees with no work performed
    // subtreeFlags is a bitmask - if 0, no child fibers have work
    const hasSubtreeWork = fiber.subtreeFlags !== 0 || fiber.flags !== 0;
    if (fiber.tag === ContextProvider) {
        // Provider fiber's type is the context object
        const contextType = fiber.type;
        if (contextType) {
            // Get the context from _context property (Provider.type._context)
            const context = contextType._context;
            if (context && !contextRegistry.has(context)) {
                // Try to get name from various sources
                const name = getProviderContextName(contextType, fiber);
                if (name) {
                    contextRegistry.set(context, name);
                }
            }
        }
    }
    // Only traverse children if subtree has work
    if (hasSubtreeWork) {
        registerContextProviders(fiber.child);
    }
    registerContextProviders(fiber.sibling);
}
/**
 * Extract context name from Provider fiber
 */
function getProviderContextName(contextType, fiber) {
    // 1. Check displayName on the context type
    if (contextType.displayName && typeof contextType.displayName === "string") {
        return contextType.displayName.replace(/\.Provider$/, "");
    }
    // 2. Check _context.displayName
    const innerContext = contextType._context;
    if (innerContext?.displayName && typeof innerContext.displayName === "string") {
        return innerContext.displayName;
    }
    // 3. Try to infer from Provider's parent component name
    // E.g., OmnisearchProvider wrapping Context.Provider
    let parent = fiber.return;
    let depth = 0;
    while (parent && depth < 5) {
        depth++;
        if (isComponentFiber(parent)) {
            const parentName = getDisplayName(parent.type);
            if (parentName && parentName.endsWith("Provider")) {
                return parentName.replace(/Provider$/, "Context");
            }
            if (parentName && !shouldSkipFiber(parentName)) {
                return parentName + "Context";
            }
        }
        parent = parent.return;
    }
    // 4. Try to infer from the context value structure
    const value = innerContext?._currentValue ?? contextType._currentValue;
    if (value && typeof value === "object") {
        const keys = Object.keys(value);
        // Look for distinctive keys that hint at the context's purpose
        const hintKeys = keys.filter((k) => !["children", "value", "Provider", "Consumer"].includes(k));
        if (hintKeys.length > 0 && hintKeys.length <= 3) {
            return `Context(${hintKeys.slice(0, 3).join(",")})`;
        }
    }
    return null;
}
/**
 * Try to extract a meaningful name from a context object
 */
function getContextName(ctx) {
    if (!ctx || typeof ctx !== "object")
        return "Context";
    // First, check our registry (populated by registerContextProviders)
    const registeredName = contextRegistry.get(ctx);
    if (registeredName) {
        return registeredName;
    }
    const c = ctx;
    // Check displayName on context itself
    if (c.displayName && typeof c.displayName === "string") {
        return c.displayName;
    }
    // Check Provider.displayName (React pattern)
    const provider = c.Provider;
    if (provider?.displayName && typeof provider.displayName === "string") {
        return provider.displayName.replace(/\.Provider$/, "");
    }
    // Check _context (internal React reference)
    const inner = c._context;
    if (inner?.displayName && typeof inner.displayName === "string") {
        return inner.displayName;
    }
    // Try to infer from the value structure
    const value = c._currentValue;
    if (value && typeof value === "object") {
        const keys = Object.keys(value);
        if (keys.length > 0 && keys.length <= 3) {
            return `Context(${keys.join(",")})`;
        }
    }
    return "Context";
}
function getChangedContexts(fiber) {
    if (!fiber.alternate)
        return [];
    const changes = [];
    let prevCtx = fiber.alternate.dependencies?.firstContext ?? null;
    let nextCtx = fiber.dependencies?.firstContext ?? null;
    while (prevCtx && nextCtx) {
        // Check if context value changed using Object.is (like React does)
        if (!Object.is(prevCtx.memoizedValue, nextCtx.memoizedValue)) {
            const name = getContextName(nextCtx.context);
            changes.push({ name });
        }
        prevCtx = prevCtx.next;
        nextCtx = nextCtx.next;
    }
    return changes;
}
/**
 * Detect hook type from its structure (based on React DevTools patterns)
 * Returns a descriptive string like "useState", "useMemo(array)", etc.
 */
function detectHookType(hook) {
    const state = hook.memoizedState;
    // useState/useReducer: has queue with dispatch function
    // Queue is on the hook object, not memoizedState
    if (hook.queue && typeof hook.queue.dispatch === "function") {
        // Distinguish: useReducer has lastRenderedReducer
        if (hook.queue.lastRenderedReducer) {
            return "useReducer";
        }
        // Try to hint at the state type
        const stateHint = getValueTypeHint(state);
        return stateHint ? `useState<${stateHint}>` : "useState";
    }
    // useRef: memoizedState is object with 'current' property
    if (state && typeof state === "object" && !Array.isArray(state)) {
        if ("current" in state) {
            const refValue = state.current;
            const hint = getValueTypeHint(refValue);
            return hint ? `useRef<${hint}>` : "useRef";
        }
        // useEffect/useLayoutEffect: memoizedState has tag, create, destroy, deps, next
        const effectState = state;
        if ("create" in effectState &&
            "destroy" in effectState &&
            "deps" in effectState &&
            "tag" in effectState) {
            // Tag indicates layout vs passive effect
            const tag = effectState.tag;
            // Layout effects have HookLayout flag (0b0100 = 4)
            if ((tag & 4) !== 0) {
                return "useLayoutEffect";
            }
            return "useEffect";
        }
    }
    // useMemo/useCallback: memoizedState is [value, deps] array
    if (Array.isArray(state) && state.length === 2) {
        const [value, deps] = state;
        if (deps === null || Array.isArray(deps)) {
            // Distinguish useMemo from useCallback by checking if value is a function
            if (typeof value === "function") {
                return "useCallback";
            }
            const hint = getValueTypeHint(value);
            return hint ? `useMemo<${hint}>` : "useMemo";
        }
    }
    // useTransition: memoizedState is boolean (isPending)
    if (typeof state === "boolean") {
        return "useTransition";
    }
    // useId: memoizedState is a string starting with :
    if (typeof state === "string" && state.startsWith(":")) {
        return "useId";
    }
    // useDeferredValue: memoizedState is the deferred value
    // (hard to distinguish without more context)
    return "hook";
}
/**
 * Get a type hint for a value (for more descriptive hook names)
 */
function getValueTypeHint(value) {
    if (value === null)
        return "null";
    if (value === undefined)
        return null;
    if (Array.isArray(value)) {
        const len = value.length;
        if (len === 0)
            return "[]";
        // Show first element type if homogeneous
        const firstType = typeof value[0];
        if (len <= 3 && value.every((v) => typeof v === firstType)) {
            return `${firstType}[${len}]`;
        }
        return `array[${len}]`;
    }
    if (typeof value === "object") {
        const keys = Object.keys(value);
        if (keys.length === 0)
            return "{}";
        if (keys.length <= 2) {
            return `{${keys.join(",")}}`;
        }
        // Check for common object patterns
        const proto = Object.getPrototypeOf(value);
        if (proto?.constructor?.name && proto.constructor.name !== "Object") {
            return proto.constructor.name;
        }
        return `object`;
    }
    if (typeof value === "function")
        return "fn";
    if (typeof value === "boolean")
        return "bool";
    if (typeof value === "number")
        return "number";
    if (typeof value === "string") {
        if (value.length > 20)
            return "string";
        return null; // Don't hint short strings
    }
    return null;
}
function getChangedHooks(fiber) {
    if (!fiber.alternate)
        return [];
    const changes = [];
    let prevHook = fiber.alternate.memoizedState;
    let nextHook = fiber.memoizedState;
    let index = 0;
    while (prevHook && nextHook) {
        // Compare hook state values using Object.is (like React does)
        if (!Object.is(prevHook.memoizedState, nextHook.memoizedState)) {
            const type = detectHookType(nextHook);
            changes.push({ index, type });
        }
        prevHook = prevHook.next;
        nextHook = nextHook.next;
        index++;
    }
    return changes;
}
function diffProps(prevProps, nextProps) {
    if (!prevProps || !nextProps)
        return [];
    const changes = [];
    const allKeys = new Set([...Object.keys(prevProps), ...Object.keys(nextProps)]);
    for (const key of allKeys) {
        if (key === "children" || key === "ref" || key === "key")
            continue;
        const prev = prevProps[key];
        const next = nextProps[key];
        if (prev !== next) {
            let refOnly = false;
            if (typeof prev === "object" && typeof next === "object" && prev !== null && next !== null) {
                try {
                    refOnly = JSON.stringify(prev) === JSON.stringify(next);
                }
                catch {
                    /* ignore */
                }
            }
            changes.push({ key, refOnly });
        }
    }
    return changes;
}
/**
 * Collect all parent names up the fiber tree (for causality logging)
 */
function collectParentChain(fiber) {
    const parents = [];
    let parent = fiber.return;
    let depth = 0;
    while (parent && depth < 100) {
        depth++;
        if (isComponentFiber(parent)) {
            const name = getDisplayName(parent.type);
            if (name && !shouldSkipFiber(name)) {
                parents.push(name);
            }
        }
        parent = parent.return;
    }
    return parents;
}
/**
 * Collect render data for a fiber (without logging yet)
 */
function collectRenderData(fiber) {
    const name = getDisplayName(fiber.type);
    if (!name || shouldSkipFiber(name))
        return null;
    if (!didFiberRender(fiber))
        return null;
    const parentName = getParentComponentName(fiber);
    const isMount = fiber.alternate === null;
    // Detect all types of changes
    const propChanges = isMount
        ? []
        : diffProps(fiber.alternate?.memoizedProps ?? null, fiber.memoizedProps);
    const contextChanges = isMount ? [] : getChangedContexts(fiber);
    const hookChanges = isMount ? [] : getChangedHooks(fiber);
    const changedKeys = propChanges.map((c) => c.key);
    const refOnlyKeys = propChanges.filter((c) => c.refOnly).map((c) => c.key);
    // Determine primary reason (priority: mount > context > hooks > props > parent)
    let reasonType;
    if (isMount) {
        reasonType = { type: "mount" };
    }
    else if (contextChanges.length > 0) {
        // Context changed
        const contextNames = contextChanges.map((c) => c.name);
        reasonType = {
            type: "context",
            name: contextNames.join(", "),
            keys: contextNames,
        };
    }
    else if (hookChanges.length > 0 && changedKeys.length === 0) {
        // Hook state changed (but not props)
        const hookKeys = hookChanges.map((h) => `${h.type}[${h.index}]`);
        reasonType = { type: "state", keys: hookKeys };
    }
    else if (changedKeys.length > 0) {
        // Props changed
        reasonType = {
            type: "props",
            keys: changedKeys,
            refOnly: refOnlyKeys.length > 0,
        };
    }
    else {
        // No detectable changes - parent triggered
        reasonType = { type: "parent" };
    }
    return {
        name,
        parentName,
        reasonType,
        changedKeys,
        refOnly: refOnlyKeys.length === changedKeys.length && refOnlyKeys.length > 0,
        duration: fiber.actualDuration,
    };
}
/**
 * Traverse fiber tree and collect all rendered components.
 * OPTIMIZED: Uses subtreeFlags to skip subtrees with no work.
 */
function collectRenderedFibers(fiber, collected) {
    if (!fiber)
        return;
    // OPTIMIZATION: Check if this fiber or its subtree has work
    const hasWork = fiber.flags !== 0;
    const hasSubtreeWork = fiber.subtreeFlags !== 0;
    // Only process this fiber if it has work
    if (hasWork && isComponentFiber(fiber)) {
        const data = collectRenderData(fiber);
        if (data) {
            // Use name as key (may overwrite if same component renders multiple times)
            collected.set(data.name, { fiber, data });
        }
    }
    // Only traverse children if subtree has work
    if (hasSubtreeWork) {
        collectRenderedFibers(fiber.child, collected);
    }
    collectRenderedFibers(fiber.sibling, collected);
}
/**
 * Process a fiber commit - called via requestIdleCallback to avoid blocking main thread.
 */
function processCommit(fiber) {
    // Register context providers first (so we can look up names during render detection)
    registerContextProviders(fiber);
    // Collect all rendered fibers first
    const renderedFibers = new Map();
    collectRenderedFibers(fiber, renderedFibers);
    if (renderedFibers.size === 0)
        return;
    // Gate 1: Must have watched components
    const watchedComponents = bolt.getWatchedComponents();
    if (watchedComponents.length === 0)
        return;
    // Gate 2: Must be in active interaction OR always-track mode
    if (!bolt.isActive() && !bolt.isAlwaysTracking())
        return;
    // Generate a new commit ID for this React commit (for grouping renders)
    const commitId = bolt.newCommit();
    const watchedSet = new Set(watchedComponents);
    const toLog = new Set();
    // Find watched components that rendered
    for (const [name, { fiber: f }] of renderedFibers) {
        if (watchedSet.has(name)) {
            toLog.add(name);
            // Also add parent chain for causality
            const parents = collectParentChain(f);
            for (const parent of parents) {
                if (renderedFibers.has(parent)) {
                    toLog.add(parent);
                }
            }
        }
    }
    // Log only the filtered components
    let sequence = 0;
    for (const name of toLog) {
        const entry = renderedFibers.get(name);
        if (!entry)
            continue;
        const { data } = entry;
        bolt.logRender({
            componentName: data.name,
            reasonType: data.reasonType,
            reasonKeys: data.changedKeys,
            refOnly: data.refOnly,
            parentSpanId: data.parentName ?? undefined,
            durationMs: data.duration,
            commitId,
            commitSequence: sequence++,
        });
    }
}
// Queue for pending commits to process during idle time
const pendingCommits = [];
let idleCallbackScheduled = false;
/**
 * Process pending commits during idle time.
 * Uses requestIdleCallback to avoid blocking the main thread.
 */
function processPendingCommits(deadline) {
    idleCallbackScheduled = false;
    // Process commits while we have time remaining
    while (pendingCommits.length > 0 && deadline.timeRemaining() > 0) {
        const fiber = pendingCommits.shift();
        if (fiber) {
            processCommit(fiber);
        }
    }
    // If there are still pending commits, schedule another idle callback
    if (pendingCommits.length > 0) {
        scheduleIdleCallback();
    }
}
/**
 * Schedule an idle callback if not already scheduled.
 */
function scheduleIdleCallback() {
    if (!idleCallbackScheduled && typeof requestIdleCallback !== "undefined") {
        idleCallbackScheduled = true;
        requestIdleCallback(processPendingCommits, { timeout: 1000 });
    }
}
function handleCommitFiberRoot(_rendererID, root, _priorityLevel) {
    // Early bail-out checks (cheap) - do these synchronously
    const watchedComponents = bolt.getWatchedComponents();
    if (watchedComponents.length === 0)
        return;
    if (!bolt.isActive() && !bolt.isAlwaysTracking())
        return;
    // Defer the expensive work to idle time
    const fiber = root.current;
    // Check if there's any work in the tree at all
    if (fiber.flags === 0 && fiber.subtreeFlags === 0)
        return;
    if (typeof requestIdleCallback !== "undefined") {
        // Queue for processing during idle time
        pendingCommits.push(fiber);
        scheduleIdleCallback();
    }
    else {
        // Fallback for environments without requestIdleCallback (e.g., older browsers, tests)
        processCommit(fiber);
    }
}
// ============================================
// Hook setup (before React loads)
// ============================================
// Check if we're in a browser environment
const isBrowser = typeof window !== "undefined";
// Check if tracking should be enabled
// Note: In the package context, we can't use import.meta.env.DEV
// so we check for NODE_ENV instead
const isDev = typeof process !== "undefined" ? process.env.NODE_ENV !== "production" : true;
const trackingEnabled = isBrowser && isDev;
if (trackingEnabled && isBrowser) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const existingHook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
    if (!existingHook) {
        // Create minimal hook
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.__REACT_DEVTOOLS_GLOBAL_HOOK__ = {
            renderers: new Map(),
            supportsFiber: true,
            inject: () => 1,
            onScheduleFiberRoot: () => { },
            onCommitFiberRoot: handleCommitFiberRoot,
            onCommitFiberUnmount: () => { },
            onPostCommitFiberRoot: () => { },
        };
    }
    else {
        // Wrap existing hook
        const originalOnCommit = existingHook.onCommitFiberRoot;
        existingHook.onCommitFiberRoot = (rendererID, root, priorityLevel) => {
            handleCommitFiberRoot(rendererID, root, priorityLevel);
            if (typeof originalOnCommit === "function") {
                originalOnCommit(rendererID, root, priorityLevel);
            }
        };
    }
}
// Export for compatibility (not needed with new approach)
export function getParentFromFiber(_componentName) {
    return null; // Deprecated - parent is now logged directly during traversal
}
//# sourceMappingURL=devtools-hook.js.map